/* eslint-disable max-lines */
import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

import { AngleDownIcon, CalendarIcon, CloseIcon } from '@wf-sl/common-icons'
import { isNumber } from '@wf-sl/common-utilities'

import { isValidDate, isValidDateOrRange } from '../../util/date'
import T3Constants from '../constants'
import FormField from '../form/form-field'

import '@wf/react-daterange-picker/dist/react-calendar.css'

import { Popover } from '../popover/popover'
import { MODIFIERS_NO_ARROW } from '../popover/popover-utils'
import TextField from '../textfield/textfield'
import DatePickerPopover from './datepicker-popover'
import { createDefaultIntl } from './intl'
import { toMoment, toMomentRange } from './moment'
import * as customPropTypes from './prop-types'

import styles from './datepicker.module.css'

const labelsProp = PropTypes.shape({
    apply: PropTypes.string,
    cancel: PropTypes.string,
    timeToggle: PropTypes.string
})

const propTypes = {
    allDay: PropTypes.bool,
    allDayDisabled: PropTypes.bool,
    allDayTooltipContent: PropTypes.string,
    autoAllDay: PropTypes.bool,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    containerClassName: PropTypes.string,
    disableYearMonthChoiceArrows: PropTypes.bool,
    disabled: PropTypes.bool,
    emptyTimeField: PropTypes.bool,
    error: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object // eslint-disable-line react/forbid-prop-types
    ]),
    initialMonth: PropTypes.number,
    initialYear: PropTypes.number,
    intl: customPropTypes.intl,
    labels: labelsProp,
    limitPeriod: PropTypes.number,
    locale: PropTypes.string,
    max: PropTypes.instanceOf(Date),
    min: PropTypes.instanceOf(Date),
    noPopoverRemount: PropTypes.bool,
    onChange: PropTypes.func,
    onReset: PropTypes.func,
    pickerPosition: PropTypes.string,
    placeholder: PropTypes.string,
    popoverClassName: PropTypes.string,
    predefinedPeriods: PropTypes.arrayOf(PropTypes.element),
    rangeSelection: PropTypes.bool,
    showCurrentMonth: PropTypes.oneOf(['first', 'last']),
    textfieldClassName: PropTypes.string,
    timeSelection: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), customPropTypes.dateRange, PropTypes.string])
}

const defaultProps = {
    allDay: false,
    allDayDisabled: false,
    allDayTooltipContent: '',
    autoAllDay: true,
    clearable: false,
    emptyTimeField: false,
    noPopoverRemount: false,
    onChange: () => {},
    onReset: () => {},
    pickerPosition: 'bottom',
    placeholder: T3Constants.HtmlEntities.MDASH,
    rangeSelection: false,
    showCurrentMonth: 'last',
    timeSelection: false
}

/**
 * Main date-picker component consisting of the textfield and the popover
 * which contains the actual picker widget, the predefined periods, etc.
 *
 * DatePicker's API (props) to the outside world are based on Date and
 * `{ start: Date, end: Date }` as data types for the `value`. Internally it
 * will always use `moment.range()` for the sake of a consistent data model.
 *
 * Known limitations:
 * - Time selection (when range selection):
 *   Since there is no `24:00` (esp. in 12-hour-clock), the range's maximum time
 *   in a day is 23:59:59.999. Since the backend treats intervals as `[from, to)`
 *   ("from" inclusive, "to" exclusive) the selected interval will not cover the
 *   day's very last millisecond.
 *
 * @author Andy Wermke
 * @example
 * <DatePicker
 *     value={someDate} onChange={onChange}
 *     min={minimumDate} max={maximumDate}
 *     predefinedPeriods={[ <Option title='Today' value={new Date(Date.now())} /> ]} />
 */
class DatePicker extends FormField {
    constructor(props) {
        super(props)

        this.state = {
            showPicker: false
        }

        this.intl = Object.assign({}, createDefaultIntl(props.locale), this.props.intl)

        this.popover = null
        this.textfield = null

        this.hidePicker = this.hidePicker.bind(this)
        this.onChange = this.onChange.bind(this)
        this.onGlobalClick = this.onGlobalClick.bind(this)
        this.onReset = this.onReset.bind(this)
        this.togglePicker = this.togglePicker.bind(this)
    }

    UNSAFE_componentWillMount() {
        document.addEventListener('click', this.onGlobalClick)
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onGlobalClick)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.intl !== this.props.intl || nextProps.locale !== this.props.locale) {
            this.intl = Object.assign({}, createDefaultIntl(nextProps.locale), nextProps.intl)
        }
    }

    render() {
        const { disabled, error, locale, pickerPosition, rangeSelection } = this.props
        const { popoverClassName, textfieldClassName } = this.props
        const { showPicker } = this.state
        const value = toMomentRange(this.props.value, locale, rangeSelection)
        const showTime = this.shouldShowTime(value)

        const inputClassName = cx(styles['textfield-input'], { [styles['textfield-input-placeholder']]: !value })
        const inputContainerClassName = cx(
            styles['textfield-container'],
            textfieldClassName,
            this.getInputContainerSizing(showTime)
        )
        const containerClassName = cx(
            this.props.containerClassName,
            styles['container'],
            {
                [styles['container--picker-visible']]: showPicker,
                [styles['container--disabled']]: disabled
            },
            't3sel-date-picker'
        )
        const combinedPopoverClassName = cx(styles['popover'], popoverClassName, {
            [styles['popover--visible']]: showPicker
        })

        return (
            <div className={containerClassName}>
                <Popover
                    className={combinedPopoverClassName}
                    content={this.renderDatePickerPopover(value)}
                    modifiers={MODIFIERS_NO_ARROW}
                    placement={pickerPosition}
                >
                    <TextField
                        ref={ref => {
                            this.textfield = ReactDOM.findDOMNode(ref)
                        }}
                        readOnly
                        type="text"
                        value={this.formatValue(this.intl, value, showTime)}
                        onClick={this.togglePicker}
                        containerClassName={inputContainerClassName}
                        className={inputClassName}
                        leftIcon={this.renderCalendarIcon()}
                        rightIcon={this.renderRightIcon(value)}
                        {...{ disabled, error }}
                    />
                </Popover>
            </div>
        )
    }

    renderDatePickerPopover(value) {
        const { showPicker } = this.state
        const {
            allDay,
            allDayDisabled,
            allDayTooltipContent,
            autoAllDay,
            className,
            disableYearMonthChoiceArrows,
            emptyTimeField,
            initialMonth,
            initialYear,
            intl,
            labels,
            limitPeriod,
            locale,
            max,
            min,
            noPopoverRemount,
            pickerPosition,
            predefinedPeriods,
            rangeSelection,
            showCurrentMonth,
            timeSelection
        } = this.props

        if (!showPicker && !noPopoverRemount) {
            // This is important, so the react-daterange-picker will be
            // unounted/remounted and loose its state. Also safely removes all
            // ErrorPopovers on the DatePickerPopover. But causes problems when
            // doing unit testing.
            return <div />
        }

        const combinedClassName = cx(className, styles['date-picker'], {
            [styles['date-picker--visible']]: showPicker,
            [styles['date-picker--position-top']]: pickerPosition.startsWith('top')
        })
        const defaultLabels = intl
            ? {
                  apply: intl.translate('atl_apply'),
                  cancel: intl.translate('atl_cancel'),
                  timeToggle: intl.translate('atl_all_day')
              }
            : {}

        const popoverValue = !this.props.value && isNumber(initialYear) && isNumber(initialMonth) ? null : value

        return (
            <DatePickerPopover
                ref={ref => {
                    this.popover = ReactDOM.findDOMNode(ref)
                }}
                className={combinedClassName}
                intl={this.intl}
                labels={{ ...defaultLabels, ...labels }}
                min={min ? toMoment(min, locale) : undefined}
                max={max ? toMoment(max, locale) : undefined}
                onChange={this.onChange}
                onClose={this.hidePicker}
                value={popoverValue}
                {...{
                    allDay,
                    allDayDisabled,
                    allDayTooltipContent,
                    autoAllDay,
                    disableYearMonthChoiceArrows,
                    emptyTimeField,
                    initialMonth,
                    initialYear,
                    limitPeriod,
                    locale,
                    noPopoverRemount,
                    predefinedPeriods,
                    rangeSelection,
                    showCurrentMonth,
                    timeSelection
                }}
            />
        )
    }

    renderCalendarIcon() {
        return (
            <div className={styles['calendar-icon']} onClick={this.togglePicker}>
                <CalendarIcon className={styles['calendar-icon-svg']} role="button" />
            </div>
        )
    }

    renderRightIcon(value) {
        const { clearable } = this.props
        return value && isValidDateOrRange(value) && clearable ? (
            <CloseIcon className={styles['clear-icon']} onClick={this.onReset} />
        ) : (
            <AngleDownIcon className={styles['angle-down-icon']} onClick={this.togglePicker} />
        )
    }

    onChange(value, allDay) {
        const { onChange, rangeSelection } = this.props

        this.setState({
            showPicker: false
        })

        if (rangeSelection) {
            onChange(
                {
                    end: value.end.toDate(),
                    start: value.start.toDate()
                },
                allDay
            )
        } else {
            onChange(value.start.toDate(), allDay)
        }
    }

    onGlobalClick(event) {
        if (!this.isAimedAtDatePicker(event)) {
            this.hidePicker()
        }
    }

    onReset(event) {
        const { onReset } = this.props

        stopEvent(event)
        onReset()
    }

    isAimedAtDatePicker(event) {
        const { target } = event

        if (this.textfield && (target === this.textfield || this.textfield.contains(target))) {
            return true
        } else if (this.popover && (target === this.popover || this.popover.contains(target))) {
            return true
        } else {
            return false
        }
    }

    formatValue(intl, value, showTime) {
        const { placeholder, rangeSelection } = this.props
        const values = rangeSelection ? [value.start, value.end] : [value.start]

        return values
            .map(val => (isValidDate(val) ? this.formatDate(intl, val.toDate(), showTime) : placeholder))
            .join(' – ')
    }

    formatDate(intl, value, showTime) {
        const dateValue = intl.formatDateOnly(value)
        return showTime ? `${dateValue}, ${intl.formatTimeOnly(value)}` : dateValue
    }

    shouldShowTime(value) {
        const { allDay, autoAllDay, rangeSelection, timeSelection } = this.props
        const endOfDay = () => (value.end && value.end.hour() !== 23) || (value.end && value.end.minute() !== 59)
        const startOfDay = () =>
            (value.start && value.start.hour() !== 0) || (value.start && value.start.minute() !== 0)
        const wholeDay = () => startOfDay() || (rangeSelection && endOfDay())

        return timeSelection && !allDay && !!value && (!autoAllDay || wholeDay())
    }

    showPicker() {
        const { disabled } = this.props
        if (!disabled) {
            this.setState({ showPicker: true })
        }
    }

    hidePicker() {
        if (this.state.showPicker) {
            this.setState({ showPicker: false })
        }
    }

    togglePicker(event) {
        stopEvent(event)

        if (!this.state.showPicker) {
            this.showPicker()
        } else {
            this.hidePicker()
        }
    }

    getInputContainerSizing(showTime) {
        const { rangeSelection, timeSelection } = this.props

        if (rangeSelection && timeSelection && showTime) {
            return styles['textfield-container--size-l']
        } else {
            return styles['textfield-container--size-s']
        }
    }
}

DatePicker.propTypes = propTypes
DatePicker.defaultProps = defaultProps

function stopEvent(event) {
    /*  Please keep both preventions: propagation and default
        (otherwise the datepicker sometimes triggers double trigger event on icon clicks */
    event.preventDefault()
    event.stopPropagation()
}

export { DatePicker }
